<script setup>
import PreviewPanel from '@/components/SingleFileViewComponent.vue';
import { useAxios } from '@/hooks/useAxios';
import { computed, ref, watch } from 'vue';
let BASE_URL = import.meta.env.VITE_APP_API_URL;
// Props 정의
const props = defineProps({
    modelValue: {
        type: String,
        default: ''
    },
    inputId: {
        type: String,
        default: ''
    },
    filename: {
        type: String,
        default: ''
    },
    fileId: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    disabled: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    labelRightmargin: {
        type: String,
        default: '2rem'
    },
    visible: {
        type: Boolean,
        default: true
    },
    height: {
        type: String,
        default: '3rem'
    },
    readonly: {
        type: Boolean,
        default: false
    },
    errmsg: {
        type: String,
        default: ''
    }
});

// Emits 정의
const emit = defineEmits(['update:modelValue', 'input', 'click', 'change']);
const isReadonly = computed(() => props.readonly);
const fileUrl = ref('');
const fileDownUrl = ref('');
const filenameRef = computed(() => props.filename);
const fileIdRef = computed(() => props.fileId);
const errMessage = ref(props.errmsg);
const ValidationMsg = ref('');
const previewFile = ref({
    url: '',
    type: ''
});

watch(
    () => props.fileId,
    async (newVal) => {
        fileUrl.value = '';
        fileDownUrl.value = '';
        if (newVal) {
            try {
                const url = `/upload/images/${fileIdRef.value}`;
                const config = {
                    method: 'get'
                };
                const { data, error, execute } = useAxios(url, config, {
                    immediate: false,
                    onSuccess(response) {
                        if (response.data.data.length > 0) {
                            fileUrl.value = `${BASE_URL}/view/${response.data.data[0].attachment_filename}`;
                            fileDownUrl.value = `${BASE_URL}/download/${response.data.data[0].attachment_filename}`;
                        }
                    },
                    onError(err) {
                        console.error('Error:', err.message);
                    }
                });
                execute();
            } catch (error) {
                console.error('파일 조회 오류 발생:', error);
            }
        } else {
            fileUrl.value = '';
        }
    },
    { immediate: true }
);

const handleFilePreview = async () => {
    try {
        const response = await fetch(`${fileUrl}`);
        if (response.ok) {
            previewFile.value = {
                url: fileUrl,
                type: 'image',
                downurl: fileDownUrl
            };
        }
    } catch (error) {
        console.error('Error previewing file:', error);
        toastComponentRef.value?.showToast('error', '알림', '파일 미리보기 중 오류가 발생했습니다.');
    }
};

const closePreview = () => {
    previewFile.value = { url: '', type: '' };
};

// watch(
//     () => props.filename,
//     async (newVal) => {
//         if (newVal) {
//             fileUrl.value = `${BASE_URL}/view/${newVal}`;
//             // try {
//             //     const url = `/view/${newVal}`;
//             //     const config = {
//             //         method: 'get'
//             //     };
//             //     const { data, error, execute } = useAxios(url, config, {
//             //         immediate: false,
//             //         onSuccess(response) {
//             //             const contentType = response.headers['content-type'];
//             //             if (contentType.startsWith('image/')) {
//             //                 fileUrl.value = `${BASE_URL}/view/${newVal}`;
//             //             }
//             //         },
//             //         onError(err) {
//             //             console.error('Error:', err.message);
//             //         }
//             //     });

//             //     execute();
//             // } catch (error) {}
//         } else {
//             fileUrl.value = '';
//         }
//     },
//     { immediate: true }
// );

watch(
    () => props.errmsg,
    (newVal) => {
        ValidationMsg.value = newVal || '';
        errMessage.value = newVal || '';
    },
    { immediate: true, deep: true }
);

const emitUpdate = (value) => {
    inputValue.value = value;
    emit('update:modelValue', value);
    emit('input', {
        id: props.inputId,
        value: value
    });
};
</script>

<template>
    <div>
        <div
            class="flex flex-col"
            :style="{
                marginTop: marginTop,
                marginBottom: marginBottom,
                marginLeft: marginLeft,
                marginRight: marginRight
            }"
        >
            <label class="label">
                {{ label }}
                <span v-if="required" class="required-asterisk">*</span>
                <span v-if="ValidationMsg" class="validation-message">{{ ValidationMsg }}</span>
            </label>

            <div class="relative flex items-center w-full" :style="{ height: height }">
                <img v-if="fileUrl" :src="fileUrl" :alt="label" class="preview-image" />
                <!-- @click="handleFilePreview()" -->
                <div v-else class="no-image">등록된 이미지가 없습니다.</div>
            </div>
        </div>
        <PreviewPanel v-if="previewFile.url" :fileUrl="previewFile.url" :fileType="previewFile.type" :downurl="previewFile.downurl" @close="closePreview" />
    </div>
</template>

<style scoped>
.input-wrapper {
    display: flex;
    flex-direction: column;
}

.label {
    font-family: 'SCoreDream600';
    display: flex;
    /* justify-content: space-between; */
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
}

.required-asterisk {
    color: #f87171; /* 빨간색 */
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.error-border {
    border: 1px solid red !important;
}

.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.preview-image {
    width: 100%;
    height: 100%;
    object-fit: contain;
    justify-content: center;
    align-items: center;
    border: 0.1rem solid #d1d5db;
    border-radius: 0.5rem;
    resize: none;
}

.no-image {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 0.1rem solid #d1d5db;
    border-radius: 0.5rem;
    background-color: whitesmoke;
    color: #9ca3af;
}
</style>
